import { deviceAdminApi } from '@/api';
import { HeaderBar, HelpFactoryComponent } from '@/components/common';
import { ComponentPermission, SecuredComponents, User } from '@/model/AccountModel';
import { CriteriaFunctionValue, CriteriaRequest, CriteriaSearchRequest } from '@/model/CommonModel';
import { CredentialSearchField, PersonDetailModel } from '@/model/EnrollmentModel';
import { HelpFeature } from '@/model/HelpModel';
import { useStoreDispatch, useStoreSelector } from '@/store';
import { setHavePersonChanged, setIsEnrollmentTabLoading, setIsPersonTableLoading, setPersonInformationId, setUpdateCounter } from '@/store/enrollment/actions';
import { selectPersonFullName, selectPersonId } from '@/store/enrollment/selectors';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Breadcrumb, BreadcrumbProps, Spin } from 'antd';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { CredentialTable } from '../../CredentialTable/CredentialTable';
import { buildCredentialCriteriaRequest, enrollmentGoBackTextId } from '../../helper';
import styles from './personInformationContainer.module.scss';
import { PersonInformationTabs } from './PersonInformationTabs';

let user: User = getUser();
const personPermissions: ComponentPermission = User.getComponentPermission(user, SecuredComponents.Enrollment_Person);
user = null;
const topicId: string = 'PersonInformation';

type Props = {
	personTableTitle: string;
	hidden: boolean;
	updatePivPersonData?: PersonDetailModel;
	clearUpdatePivPersonData?: () => void;
};

const PersonInformationContainer: React.FC<Props> = ({ personTableTitle, hidden, updatePivPersonData, clearUpdatePivPersonData }) => {
	//#region State Management Variables
	const dispatch = useStoreDispatch();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isCredentialTableLoading, setIsCredentialTableLoading] = useState<boolean>(true);
	const [updateCounterCredential, setUpdateCounterCredential] = useState<number>(0);
	const [reloadPersonTable, setReloadPersonTable] = useState<boolean>(false);
	const [personIdState, setPersonIdState] = useState<number>(-100);

	const handleBackTimeout = useRef<NodeJS.Timeout>();
	const personIdReference = useRef<number>(personIdState);
	const setPersonIdReference = (personId: number): void => {
		personIdReference.current = personId;
		setPersonIdState(personId);
	};

	const personFullName: string = useStoreSelector<string>(selectPersonFullName);
	const personId: number = useStoreSelector<number>(selectPersonId);
	//#endregion

	useEffect(() => {
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			handleBeforeUnload();
			window.removeEventListener('beforeunload', handleBeforeUnload);
			if (handleBackTimeout.current) {
				clearTimeout(handleBackTimeout.current);
			}
		};
	}, []);

	useEffect(() => {
		setPersonIdReference(personId);
	}, [personId]);

	const handleBeforeUnload = (): void => {
		deviceAdminApi.unlockComponentDeviceAdmin(personIdReference.current, SecuredComponents.Enrollment_Person);
	};

	const handleOnClickBack = (reloadPersonTable: boolean): void => {
		setIsLoading(true);
		handleBackTimeout.current = setTimeout(() => {
			dispatch(setPersonInformationId(0));
			dispatch(setIsEnrollmentTabLoading(false));
			dispatch(setHavePersonChanged(false));
			dispatch(setIsPersonTableLoading(true));
			if (reloadPersonTable) {
				dispatch(setUpdateCounter());
			}
		}, 0);
	};

	const personIdCriteriaRequest: CriteriaRequest = buildCredentialCriteriaRequest(
		personId.toString(),
		CredentialSearchField.PersonId,
		CriteriaFunctionValue.equalTo
	);

	const criteriaSearchRequest: CriteriaSearchRequest = { CriteriaRequest: [personIdCriteriaRequest] };
	const isNewPerson: boolean = personId === -100 || personId === 0;
	const style: CSSProperties = hidden ? { display: 'none' } : {};
	const goBackLabelId: string = hidden ? '' : enrollmentGoBackTextId;
	const personInformationReadOnly: string = personPermissions.canUpdate ? '' : `(${_('ReadOnly')})`;
	const items: BreadcrumbProps['items'] = [
		{
			title: (
				<a onClick={() => handleOnClickBack(reloadPersonTable)}>
					<ArrowLeftOutlined /> <label id={goBackLabelId}>{personTableTitle}</label>
				</a>
			),
		},
		{
			title: personFullName,
		},
	];

	return (
		<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='large'>
			<div className={styles.container} style={style}>
				<div>
					<Breadcrumb items={items} />
				</div>
				{!isNewPerson && (
					<div className={styles.credentialTable}>
						<CredentialTable
							userId={personId}
							criteriaSearchRequest={criteriaSearchRequest}
							isLoading={isCredentialTableLoading}
							setIsLoading={setIsCredentialTableLoading}
							setUpdateCounter={() => setUpdateCounterCredential(prevState => prevState + 1)}
							updateCounter={updateCounterCredential}
							scrollHeight={'16rem'}
						/>
					</div>
				)}
				<HeaderBar
					title={`${_('PersonInformation')} ${personInformationReadOnly}`}
					buttons={<HelpFactoryComponent helpFeature={HelpFeature.Enrollment} topicId={topicId} className={styles.helpButton} />}
				/>
				<PersonInformationTabs
					setReloadPersonTable={() => setReloadPersonTable(true)}
					handleOnDeletePerson={handleOnClickBack}
					updatePivPersonData={updatePivPersonData}
					clearUpdatePivPersonData={clearUpdatePivPersonData}
				/>
			</div>
		</Spin>
	);
};

export { PersonInformationContainer };
